<template>
  <b-modal
    id="page-calendar-modal"
    :ok-title="trans(block && block.id ? 'save' : 'create')"
    :cancel-title="trans('cancel')"
    size="xl"
    no-enforce-focus
    @shown="onShown"
    @hidden="shown = false"
    @ok="onSave"
  >
    <template v-slot:modal-title>
      <span>{{ trans('calendar') }}</span>
      <small v-if="!!block" class="text-muted ml-2">
        #{{ block.id }}
      </small>
    </template>

    <b-row>
      <b-col class="col-3">
        <b-form-group :label="trans('title')">
          <b-form-input v-model="text" size="sm" :disabled="busy"/>
        </b-form-group>
        <b-form-group :class="!use_today ? 'mb-1' : ''">
          <b-form-radio-group v-model="use_today" @change="onClickUseDate">
            <b-form-radio :value="false">{{ trans('date') }}</b-form-radio>
            <b-form-radio :value="true">{{ trans('today') }}</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group v-if="!use_today">
          <datetime
            v-model="date"
            :config="dateConfig"
            :placeholder="trans('date')"
            class="form-control form-control-sm"
          ></datetime>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox-group
            v-model="views"
            :options="viewsOptions"
            :state="viewsState"
            size="sm"
            :disabled="busy"
          ></b-form-checkbox-group>
          <b-form-invalid-feedback :state="viewsState">
            {{ trans('please_select_at_least_one') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="trans('default')">
          <b-form-select
            v-model="view"
            :options="viewOptions"
            :state="viewState"
            size="sm"
            :disabled="busy"
          ></b-form-select>
        </b-form-group>
        <b-form-group :label="trans('default_mobile')">
          <b-form-select
            v-model="view_mobile"
            :options="viewOptions"
            :state="viewStateMobile"
            size="sm"
            :disabled="busy"
          ></b-form-select>
        </b-form-group>
        <b-form-group :label="trans('min_calendar_time')">
          <datetime
            v-model="min_time"
            :config="timeConfig"
            class="form-control form-control-sm"
            :placeholder="moment('09', 'HH').format('LT')"
            :disabled="busy"
          />
        </b-form-group>
        <b-form-group :label="trans('max_calendar_time')">
          <datetime
            v-model="max_time"
            :config="timeConfig"
            class="form-control form-control-sm"
            :placeholder="moment('22', 'HH').format('LT')"
            :disabled="busy"
          />
        </b-form-group>

        <b-form-group :label="trans('legend') + (legend && legend.length > 0 ? ` (${legend.length})` : '')">
          <b-button variant="outline-primary" size="sm" block class="text-left"
                    v-b-modal.page-calendar-edit-legend-modal>
            <i class="fa fa-fw fa-edit"></i>
            {{ trans('edit') }}
          </b-button>
        </b-form-group>

        <div class="accordion mt-4" role="tablist">
          <b-card no-body class="mb-1" style="border: none; border-radius: 0;">
            <b-card-header header-tag="header" class="p-1 bg-white" role="tab" style="border: none;">
              <b-row>
                <b-col>
                  <strong class="d-inline-block">{{
                      trans('event_sources') + (event_sources.length > 0 ? ` (${event_sources.length})` : '')
                    }}</strong>
                  <b-button v-b-toggle.accordion-1 size="sm" class="py-0 px-1 ml-2" variant="outline-secondary">
                    <i :class="`fa fa-fw fa-eye${event_sources_visible ? '-slash' : ''}`"></i>
                  </b-button>
                </b-col>
                <b-col v-if="event_sources_visible" class="col-auto">
                  <b-button variant="outline-primary" size="sm" class="py-0 px-1" @click="onClickImportForm">
                    <i class="fa fa-fw fa-plus"></i>
                  </b-button>
                </b-col>
              </b-row>
            </b-card-header>
            <b-collapse id="accordion-1" accordion="my-accordion" role="tabpanel" v-model="event_sources_visible">
              <b-card-body class="px-0" style="max-height: 60vh; overflow-y: auto;">
                <small v-if="event_sources.length == 0" class="d-block text-muted mb-2">
                  {{ trans('event_sources_about') }}
                </small>
                <b-button
                  v-for="es in event_sources"
                  :key="'event_source_' + es.id"
                  block
                  size="sm"
                  class="mb-2 text-dark text-left"
                  @click="onClickEventSource(es)"
                  :style="`border-radius: 4px; border-color: ${es.color}; background-color: ${es.color}; color: ${ getTextColor(es) } !important;`"
                >
                  <i class="fa fa-fw fa-file-alt"></i>
                  {{ es.name }}
                </b-button>
                <b-form-checkbox size="sm" class="mt-2" switch v-model="show_participants">
                  {{ trans('show_participants') }}
                </b-form-checkbox>
                <page-calendar-edit-event-source-modal
                  :modalId="event_source_modal_id"
                  :organization="organization"
                  :page="page"
                  :block="serialized"
                  :event_source="event_source"
                  @save="onSaveEventSource"
                  @delete="onDeleteEventSource"
                ></page-calendar-edit-event-source-modal>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body class="mb-1" style="border: none; border-radius: 0;">
            <b-card-header header-tag="header" class="p-1 bg-white" role="tab" style="border: none;">
              <b-row>
                <b-col>
                  <strong class="d-inline-block">{{
                      trans('events') + (events.length > 0 ? ` (${events.length})` : '')
                    }}</strong>
                  <b-button v-b-toggle.accordion-2 size="sm" class="py-0 px-1 ml-2" variant="outline-secondary">
                    <i :class="`fa fa-fw fa-eye${events_visible ? '-slash' : ''}`"></i>
                  </b-button>
                </b-col>
                <b-col v-if="events_visible" class="col-auto">
                  <b-button variant="outline-primary" size="sm" class="py-0 px-1" @click="onClickPlusEvent">
                    <i class="fa fa-fw fa-plus"></i>
                  </b-button>
                </b-col>
              </b-row>
            </b-card-header>
            <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel" v-model="events_visible">
              <b-card-body class="px-0" style="max-height: 60vh; overflow-y: auto;">
                <small v-if="events.length == 0" class="d-block text-muted mb-2">
                  {{ trans('use_calendar_to_create_events') }}
                </small>
                <small v-if="events.length > 0" class="d-block mb-2">
                  <b-dropdown variant="link" size="sm" :disabled="busy">
                    <template #button-content>
                      <span class="text-muted">{{ trans('sort') }}:</span>
                      <i v-if="sort_dir == 'desc' && !!sort_by" class="fa fa-fw fa-arrow-up"></i>
                      <i v-if="sort_dir == 'asc' && !!sort_by" class="fa fa-fw fa-arrow-down"></i>
                      <span v-if="!sort_by" class="text-muted"><em>{{ trans('none') }}</em></span>
                      <span v-if="sort_by == 'start'">{{ trans('date') }}</span>
                      <span v-if="sort_by == 'title'">{{ trans('title') }}</span>
                    </template>
                    <b-dropdown-item @click="sort_by = null; sort_dir = 'asc'">
                      <em>{{ trans('none') }}</em>
                    </b-dropdown-item>
                    <b-dropdown-item @click="sort_by = 'title'; sort_dir = 'asc'">
                      <i class="fa fa-fw fa-arrow-down"></i>
                      {{ trans('title') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="sort_by = 'title'; sort_dir = 'desc'">
                      <i class="fa fa-fw fa-arrow-up"></i>
                      {{ trans('title') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="sort_by = 'start'; sort_dir = 'asc'">
                      <i class="fa fa-fw fa-arrow-down"></i>
                      {{ trans('date') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="sort_by = 'start'; sort_dir = 'desc'">
                      <i class="fa fa-fw fa-arrow-up"></i>
                      {{ trans('date') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </small>
                <b-button
                  v-for="e in sortedEvents"
                  :key="'event_' + e.id"
                  block
                  size="sm"
                  class="mb-2 text-dark text-left"
                  @click="onClickEvent(e)"
                  :style="`border-radius: 4px; color: ${ getTextColor(e) }!important; border-color: ${e.color}; background-color: ${e.color}`"
                >
                  <strong>{{ e.title }}</strong>
                  <small class="ml-2">{{
                      e.allDay ? dateRangeStr(e.start, e.end) : timeRangeStr(e.start, e.end)
                    }}</small>
                </b-button>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
        <b-form-group class="my-4 px-1" :label="trans('i_calendar')">
          <b-form-checkbox v-model="export_calendar" name="check-button" switch>
            {{ trans('allow_export') }}
          </b-form-checkbox>
          <b-form-text v-if="export_calendar">
            {{ trans('microsite_i_calendar_desc') }}'
          </b-form-text>
        </b-form-group>

        <p v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </p>
        <p v-if="error" class="text-danger">
          {{ error }}
        </p>
      </b-col>
      <b-col class="col-9">
        <page-calendar-block
          v-if="shown"
          ref="calendarBlock"
          :organization="organization"
          :page="page"
          :block="serialized"
          :show-participants="show_participants"
          is-preview
          @click="onClickEventInCalendar"
          @select="onSelect"
          @unselect="onUnselect"
        ></page-calendar-block>
      </b-col>
    </b-row>
    <page-calendar-edit-event-modal
      :modal-id="event_modal_id"
      :organization="organization"
      :event="event"
      @save="onSaveEvent"
      @delete="onDeleteEvent"
    ></page-calendar-edit-event-modal>
    <page-calendar-edit-legend-modal
      :organization="organization"
      v-model="legend"
      :legend-position="legend_position"
      @legend-position="legend_position = $event"
    ></page-calendar-edit-legend-modal>
  </b-modal>
</template>

<script>
import PageCalendarBlock from './PageCalendarBlock'
import Datetime from 'vue-bootstrap-datetimepicker'
import PageCalendarEditEventModal from './PageCalendarEditEventModal'
import PageCalendarEditEventSourceModal from './PageCalendarEditEventSourceModal'
import PageCalendarEditLegendModal from './PageCalendarEditLegendModal'

export default {
  props: {
    organization: Object,
    page: Object,
    block: Object,
    after: Number,
  },
  components: {
    PageCalendarBlock,
    Datetime,
    PageCalendarEditEventModal,
    PageCalendarEditEventSourceModal,
    PageCalendarEditLegendModal,
  },
  data() {
    return {
      trans: trans,
      moment: moment,
      dateRangeStr: _.dateRangeStr,
      timeRangeStr: _.timeRangeStr,
      busy: false,
      error: null,
      shown: false,
      text: trans('calendar'), // title
      use_today: false,
      date: moment().startOf('day').toISOString(),
      dateConfig: {
        locale: window.lang,
        format: 'YYYY-MM-DD',
        showTodayButton: true,
      },
      min_time: moment('09:00', 'HH:mm').format('LT'),
      max_time: moment('22:00', 'HH:mm').format('LT'),
      timeConfig: {
        locale: window.lang,
        format: 'LT',
        showClear: true,
        stepping: 30,
      },
      view: 'dayGridMonth',
      view_mobile: 'timeGridDay',
      views: ['dayGridMonth', 'timeGridWeek', 'timeGridDay'],
      viewsOptions: [
        {value: 'dayGridMonth', text: trans('month')},
        {value: 'timeGridWeek', text: trans('week')},
        {value: 'timeGridDay', text: trans('day')},
      ],
      events_visible: false,
      events: [],
      event: null,
      event_modal_id: 'page-calendar-edit-event-modal',
      event_sources_visible: false,
      event_sources: [],
      event_source: null,
      event_source_modal_id: 'page-calendar-edit-event-source-modal',
      legend: null,
      legend_position: 'bottom',
      sort_by: null,
      sort_dir: 'asc',
      export_calendar: false,
      show_participants: this.$refs.calendarBlock?.meta?.show_participants ?? false
    }
  },
  computed: {
    viewOptions() {
      return _.reduce(this.viewsOptions, (options, option) => {
        if (this.views.indexOf(option.value) >= 0) options.push(option)
        return options
      }, [])
    },
    viewsState() {
      return this.views.length == 0 ? false : null
    },
    viewState() {
      if (!this.view) return false
      return this.views.indexOf(this.view) === -1 ? false : null
    },
    viewStateMobile() {
      if (!this.view_mobile) return false
      return this.views.indexOf(this.view_mobile) === -1 ? false : null
    },
    minTime() {
      return this.min_time ?
        moment(this.min_time, 'LT').format('HH:mm:ss') :
        '09:00:00'
    },
    maxTime() {
      return this.max_time ?
        moment(this.max_time, 'LT').format('HH:mm:ss') :
        '22:00:00'
    },
    sortedEvents() {
      if (!this.sort_by) return this.events
      let events = _.sortBy(this.events, this.sort_by)
      return this.sort_dir == 'desc' ? _.reverse(events) : events
    },
    serialized() {
      return {
        type: 'calendar',
        text: this.text,
        after: this.after,
        meta: {
          date: !this.use_today ? this.date : null,
          minTime: this.minTime,
          maxTime: this.maxTime,
          view: this.view,
          view_mobile: this.view_mobile,
          views: this.views,
          events: this.events,
          events_sort_by: this.sort_by,
          events_sort_dir: this.sort_dir,
          event_sources: this.event_sources,
          legend: this.legend,
          legend_position: this.legend_position,
          export_calendar: this.export_calendar,
          show_participants: this.show_participants
        },
      }
    },
  },
  methods: {
    onClickUseDate() {
      if (!this.use_today && !this.date) {
        this.date = moment().startOf('day').toISOString()
      }
    },
    onSaveEvent(event) {
      let index = event.id !== null ?
        _.findIndex(this.events, {id: event.id}) :
        -1
      if (index >= 0) {
        this.events.splice(index, 1, event)
      } else {
        event.id = _.reduce(this.events || [], (max, e) => {
          if (e.id >= max) max = e.id + 1
          return max
        }, 0)
        this.events.push(event)
      }
      this.event = event
    },
    onDeleteEvent(event) {
      let index = event.id !== null ?
        _.findIndex(this.events, {id: event.id}) :
        -1
      if (index >= 0) {
        this.events.splice(index, 1)
      }
      this.event = null
    },
    onClickEvent(event) {
      this.event = event
      this.$bvModal.show(this.event_modal_id)
    },
    onClickEventInCalendar(event) {
      if (event.extendedProps.type == 'custom') {
        let id = parseInt(event.id, 10)
        this.event = _.find(this.events, {id: id})
        this.$bvModal.show(this.event_modal_id)
      }
    },
    onClickPlusEvent() {
      let allDay = this.$refs.calendarBlock.view == 'dayGridMonth'
      let start = moment(this.$refs.calendarBlock.date)
      start.set({
        hour: moment().startOf('hour').add(1, 'hour').hour(),
        minute: 0,
        second: 0,
      })
      let end = moment(start)
      let startStr = null
      let endStr = null
      if (allDay) {
        start.startOf('day')
        startStr = start.format('YYYY-MM-DD')
        endStr = startStr
      } else {
        end.add(1, 'hour')
        startStr = start.toISOString()
        endStr = end.toISOString()
      }
      this.event = {
        id: null,
        title: trans('event'),
        description: null,
        start: startStr,
        end: endStr,
        allDay: allDay,
      }
      this.$bvModal.show(this.event_modal_id)
    },
    onSelect(info) {
      this.event = {
        id: null,
        title: trans('event'),
        description: null,
        start: info.startStr,
        end: info.endStr,
        allDay: info.allDay,
      }
      this.$bvModal.show(this.event_modal_id)
    },
    onUnselect(info) {
    },
    onClickImportForm() {
      this.event_source = {
        id: null,
        name: null,
        recipient_form_id: null,
        question_id: null,
      }
      this.$bvModal.show(this.event_source_modal_id)
    },
    onClickEventSource(event_source) {
      this.event_source = event_source
      this.$bvModal.show(this.event_source_modal_id)
    },
    onSaveEventSource(event_source) {
      let index = event_source.id !== null ?
        _.findIndex(this.event_sources, {id: event_source.id}) :
        -1
      if (index >= 0) {
        this.event_sources.splice(index, 1, event_source)
      } else {
        event_source.id = _.reduce(this.event_sources || [], (max, e) => {
          if (e.id >= max) max = e.id + 1
          return max
        }, 0)
        this.event_sources.push(event_source)
      }
      this.event_source = event_source
    },
    onDeleteEventSource(event_source) {
      let index = event_source.id !== null ?
        _.findIndex(this.event_sources, {id: event_source.id}) :
        -1
      if (index >= 0) {
        this.event_sources.splice(index, 1)
      }
      this.event_source = null
    },
    onShown() {
      this.busy = false
      this.error = null
      this.text = this.block ? this.block.text : trans('calendar')
      this.date = this.block ? this.block.meta.date : null
      this.use_today == !this.date
      this.min_time = moment(this.block ? this.block.meta.minTime : '09:00:00', 'HH:mm:ss').format('LT')
      this.max_time = moment(this.block ? this.block.meta.maxTime : '22:00:00', 'HH:mm:ss').format('LT')
      this.view = this.block ? this.block.meta.view : 'dayGridMonth'
      this.view_mobile = this.block ? this.block.meta.view_mobile || 'timeGridDay' : 'timeGridDay'
      this.views = this.block ? this.block.meta.views : ['dayGridMonth', 'timeGridWeek', 'timeGridDay']
      this.events = this.block && this.block.meta.events ? _.cloneDeep(this.block.meta.events) : []
      this.sort_by = this.block && this.block.meta.events_sort_by ? this.block.meta.events_sort_by : null
      this.sort_dir = this.block && this.block.meta.events_sort_dir ? this.block.meta.events_sort_dir : 'asc'
      this.event = null
      this.event_sources = this.block && this.block.meta.event_sources ? _.cloneDeep(this.block.meta.event_sources) : []
      this.event_source = null
      this.legend = this.block && this.block.meta && this.block.meta.legend ? this.block.meta.legend : []
      this.legend_position = this.block && this.block.meta && this.block.meta.legend_position ? this.block.meta.legend_position : 'bottom'
      this.shown = true
      this.export_calendar = this.block ? this.block.meta.export_calendar : false
      this.show_participants = this.block ? this.block.meta.show_participants : false
    },
    onSave(e) {
      e.preventDefault()
      this.busy = true
      this.error = null
      let url = `/organization/${this.organization.id}/page/${this.page.id}/block`
      if (this.block && this.block.id) url += `/${this.block.id}`
      axios[this.block && this.block.id ? 'patch' : 'post'](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide('page-calendar-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
    },
    getTextColor(e) {
      return  e.color ? _.hexContrastColor(e.color) : '#0f0f0f'

    }
  },
  mounted() {
    window.pageCalendarModal = this
  },
}
</script>
